import React, { useMemo, useState } from "react";
import WorkforceOsLayout from "../../../components/workforce_os/WorkforceOsLayout";
import RebecaMiguelChatbot from "../../../components/workforce_os/RebecaMiguelChatbot";
import { ShieldCheck, AlertTriangle, XOctagon } from "lucide-react";

const SECTIONS = [
  {
    key: "credentials",
    label: "Credentials",
    items: ["License (active state)", "BLS", "ACLS / PALS (specialty)", "Specialty certifications", "Annual competencies"],
  },
  {
    key: "compliance",
    label: "Compliance",
    items: ["TB / PPD", "Flu vaccine", "MMR / Varicella", "Background check", "Drug screen"],
  },
  {
    key: "documentation",
    label: "Documentation",
    items: ["Up-to-date resume", "Skills checklist", "References (2+)", "I-9 / work authorization", "Recent W-2 / paystub"],
  },
];

const ALL = SECTIONS.flatMap((s) => s.items.map((i) => `${s.key}::${i}`));

function statusFor(pct) {
  if (pct >= 90) return { label: "GREEN — Ready", Icon: ShieldCheck, card: "bg-emerald-50 border-emerald-200", pill: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  if (pct >= 60) return { label: "YELLOW — Needs work", Icon: AlertTriangle, card: "bg-amber-50 border-amber-200", pill: "bg-amber-100 text-amber-800 border-amber-200" };
  return { label: "RED — Not ready", Icon: XOctagon, card: "bg-rose-50 border-rose-200", pill: "bg-rose-100 text-rose-800 border-rose-200" };
}

export default function MspReadiness() {
  React.useEffect(() => {
    document.title = "TRITAL MSP Readiness Hub™ | Workforce OS™";
  }, []);
  const [done, setDone] = useState(() => new Set());

  const pct = useMemo(() => Math.round((done.size / ALL.length) * 100), [done]);
  const status = statusFor(pct);
  const Icon = status.Icon;
  const missing = ALL.filter((k) => !done.has(k));

  function toggle(k) {
    const next = new Set(done);
    if (next.has(k)) next.delete(k); else next.add(k);
    setDone(next);
  }

  return (
    <WorkforceOsLayout
      breadcrumb="MSP Readiness Hub"
      title="TRITAL MSP Readiness Hub™"
      description="Evaluate MSP/VMS submission readiness in three buckets — credentials, compliance, documentation. Get a Green / Yellow / Red status in seconds."
    >
      <section className="py-10">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
          <div className="space-y-5" data-testid="msp-checklists">
            {SECTIONS.map((s) => (
              <div key={s.key} className="rounded-2xl border border-slate-200 bg-white p-6">
                <h3 className="font-display font-bold text-lg text-trital-navy">{s.label}</h3>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                  {s.items.map((item) => {
                    const k = `${s.key}::${item}`;
                    const on = done.has(k);
                    return (
                      <label
                        key={k}
                        className="inline-flex items-start gap-2 cursor-pointer text-[13.5px] text-slate-700"
                        data-testid={`msp-item-${s.key}-${item.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggle(k)}
                          className="mt-1 w-4 h-4 accent-trital-navy"
                        />
                        <span className={on ? "font-semibold text-slate-900" : ""}>{item}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <aside className="space-y-4">
            <div className={`rounded-2xl border p-6 ${status.card}`} data-testid="msp-status-card">
              <div className="flex items-center gap-2 overline text-slate-500">Readiness</div>
              <div className="mt-2 flex items-end gap-3">
                <div className="font-display font-black text-6xl tabular-nums text-trital-navy" data-testid="msp-percent">{pct}%</div>
              </div>
              <div className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold border ${status.pill}`} data-testid="msp-status-label">
                <Icon className="w-3.5 h-3.5" strokeWidth={2} /> {status.label}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h4 className="font-display font-bold text-trital-navy">Missing items</h4>
              {missing.length === 0 ? (
                <p className="mt-3 text-[13.5px] text-emerald-700">All items complete — ready for MSP/VMS submission.</p>
              ) : (
                <ul className="mt-3 text-[13.5px] text-slate-700 space-y-1.5 list-disc pl-5 max-h-[260px] overflow-y-auto" data-testid="msp-missing-list">
                  {missing.map((m) => <li key={m}>{m.split("::")[1]}</li>)}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </section>
      <RebecaMiguelChatbot variant="floating" />
    </WorkforceOsLayout>
  );
}
